// components/MyPage/NotificationSettings.jsx
import { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, FormGroup, FormControlLabel,
  Checkbox, Button, Snackbar, Alert, CircularProgress,
  useMediaQuery, List, ListItem, ListItemText, Switch
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../api/axios';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PushToggle from '../PushToggle';

export default function NotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [savedSettings, setSavedSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isDirty = useMemo(() => {
    if (settings === null || savedSettings === null) return false;
    return JSON.stringify(settings) !== JSON.stringify(savedSettings);
  }, [settings, savedSettings]);

  useEffect(() => {
    api.get('/api/notifications/settings/')
      .then(res => {
        const s = res.data || {};
        // 키가 없다면 기본값 세팅 (백엔드 기본과 맞추기) 
        const normalized = {
          comment_alert: s.comment_alert ?? true,
          reply_alert: s.reply_alert ?? true,
          like_alert: s.like_alert ?? false,
          guestbook_alert: s.guestbook_alert ?? true, // ✅ 방명록 
        };
        setSettings(normalized);
        setSavedSettings(normalized);
      })
      .catch(() => alert('알림 설정 정보를 불러오지 못했습니다.'));
  }, []);

  if (settings === null) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const handleToggle = key =>
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));

  const handleApply = () => {
    setSaving(true);
    api.post('/api/notifications/settings/update/', settings)
      .then(() => {
        setSavedSettings(settings);
        setSnack(true);
      })
      .catch(() => alert('알림 설정 저장에 실패했습니다.'))
      .finally(() => setSaving(false));
  };

  return (
    <Box
      sx={{
        mt: isMobile ? 2 : 4,
        p: isMobile ? 2 : 3,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
      }}
    >
      <Typography
        variant={isMobile ? 'subtitle1' : 'h6'}
        gutterBottom
        sx={{ fontWeight: 'bold' }}
      >
        🔔 알림 설정
      </Typography>

      {isMobile ? (
        <List>
          <ListItem divider secondaryAction={
            <Switch
              edge="end"
              onChange={() => handleToggle('comment_alert')}
              checked={settings.comment_alert}
            />
          }>
            <ListItemText primary={<><NotificationsIcon fontSize="small" sx={{ mr: 1 }} />댓글</>} />
          </ListItem>

          <ListItem divider secondaryAction={
            <Switch
              edge="end"
              onChange={() => handleToggle('reply_alert')}
              checked={settings.reply_alert}
            />
          }>
            <ListItemText primary={<><NotificationsIcon fontSize="small" sx={{ mr: 1 }} />대댓글</>} />
          </ListItem>

          <ListItem divider secondaryAction={
            <Switch
              edge="end"
              onChange={() => handleToggle('like_alert')}
              checked={settings.like_alert}
            />
          }>
            <ListItemText primary={<><NotificationsIcon fontSize="small" sx={{ mr: 1 }} />좋아요</>} />
          </ListItem>
          <ListItem divider secondaryAction={
            <Switch
              edge="end"
              onChange={() => handleToggle('guestbook_alert')}
              checked={settings.guestbook_alert}
            />
          }>
            <ListItemText primary={<><NotificationsIcon fontSize="small" sx={{ mr: 1 }} />미니홈 방명록</>} />
          </ListItem>
        </List>
      ) : (
        <FormGroup sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.comment_alert}
                onChange={() => handleToggle('comment_alert')}
              />
            }
            label="내 게시글에 댓글이 달리면 알림 받기"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.reply_alert}
                onChange={() => handleToggle('reply_alert')}
              />
            }
            label="내 댓글에 대댓글이 달리면 알림 받기"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.like_alert}
                onChange={() => handleToggle('like_alert')}
              />
            }
            label="내 글/댓글에 좋아요가 달리면 알림 받기"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.guestbook_alert}
                onChange={() => handleToggle('guestbook_alert')}
              />
            }
            label="내 미니홈 방명록에 글이 달리면 알림 받기"
          />
        </FormGroup>
      )}


      {/* ───────── Web Push 섹션 ───────── */}
      <Box sx={{ mt: 3, p: 2, borderTop: '1px dashed #e0e0e0' }}>
        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 600, mb: 1 }}>
          📲 웹 푸시(상단바 알림)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          스마트폰 상단바/PC 알림 센터에 알림을 표시합니다.
          안드로이드는 브라우저만으로, iPhone은 홈 화면에 추가(PWA 설치) 시 표시돼요.
        </Typography>

        {/* 권한/구독 ON/OFF 버튼 & 상태 */}
        <PushToggle />
      </Box>

      <Box sx={{ textAlign: isMobile ? 'center' : 'right', mt: isMobile ? 2 : 3 }}>
        <Button
          variant="contained"
          disabled={!isDirty || saving}
          onClick={handleApply}
          size={isMobile ? 'small' : 'medium'}
          fullWidth={isMobile}
        >
          {saving ? '저장 중…' : '적용하기'}
        </Button>
      </Box>

      <Snackbar
        open={snack}
        autoHideDuration={3000}
        onClose={() => setSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          알림 설정이 저장되었습니다!
        </Alert>
      </Snackbar>
    </Box>
  );
}